
import React, { useState, useEffect } from 'react';
import { Layers, Plus, Trash2, Edit2, Save, X, Loader2, Tag, AlertCircle } from 'lucide-react';
import { fetchDataCategories, createDataCategory, updateDataCategory, deleteDataCategory } from '../services/categoryService';
import { DataCategory } from '../types';

const CategoryView: React.FC = () => {
  const [categories, setCategories] = useState<DataCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form State
  const [name, setName] = useState('');
  const [keywords, setKeywords] = useState('');

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const data = await fetchDataCategories();
      setCategories(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      const keywordArray = keywords.split(',').map(k => k.trim()).filter(k => k);
      await createDataCategory(name, keywordArray);
      setName('');
      setKeywords('');
      setIsCreating(false);
      loadCategories();
    } catch (e) {
      alert("Failed to create category");
    }
  };

  const handleUpdate = async () => {
    if (!editingId || !name.trim()) return;
    try {
      const keywordArray = keywords.split(',').map(k => k.trim()).filter(k => k);
      await updateDataCategory(editingId, { name, keywords: keywordArray });
      setEditingId(null);
      setName('');
      setKeywords('');
      loadCategories();
    } catch (e) {
      alert("Failed to update category");
    }
  };

  const handleEditClick = (cat: DataCategory) => {
    setEditingId(cat.id);
    setName(cat.name);
    setKeywords(cat.keywords ? cat.keywords.join(', ') : '');
    setIsCreating(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
      await deleteDataCategory(id);
      setCategories(categories.filter(c => c.id !== id));
    } catch (e: any) {
      alert(e.message || "Failed to delete category");
    }
  };

  const resetForm = () => {
      setIsCreating(false);
      setEditingId(null);
      setName('');
      setKeywords('');
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-300">
      <div className="flex justify-between items-end border-b border-slate-700 pb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-white mb-2 flex items-center gap-3">
            <Layers className="text-indigo-500" />
            Data Categories
          </h1>
          <p className="text-slate-400">
            Organize public data into standardized taxonomies.
          </p>
        </div>
        <button 
          onClick={() => { setIsCreating(true); setEditingId(null); setName(''); setKeywords(''); }}
          className={`bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-opacity ${isCreating || editingId ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <Plus size={18} /> New Category
        </button>
      </div>

      {(isCreating || editingId) && (
        <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-xl mb-8 animate-in slide-in-from-top-2">
          <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-white">{editingId ? 'Edit Category' : 'New Category'}</h3>
              <button onClick={resetForm} className="text-slate-500 hover:text-white"><X size={20}/></button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Name</label>
              <input 
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                placeholder="e.g. Agriculture"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Keywords (Comma separated)</label>
              <input 
                value={keywords}
                onChange={e => setKeywords(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                placeholder="rice, farming, crops"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={resetForm} className="text-slate-400 hover:text-white px-4 py-2">Cancel</button>
            <button 
              onClick={editingId ? handleUpdate : handleCreate} 
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2"
            >
              <Save size={18} /> {editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-indigo-500" size={32}/></div>
      ) : (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-900 text-slate-400 font-semibold uppercase text-xs">
              <tr>
                <th className="px-6 py-4">ID</th>
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Keywords</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {categories.map((cat) => (
                <tr key={cat.id} className="hover:bg-slate-700/30 transition-colors">
                  <td className="px-6 py-4 text-slate-500 font-mono">{cat.id}</td>
                  <td className="px-6 py-4 font-bold text-white">{cat.name}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-2">
                      {cat.keywords?.map((k, i) => (
                        <span key={i} className="bg-slate-900 text-slate-300 px-2 py-1 rounded text-xs border border-slate-700 flex items-center gap-1">
                          <Tag size={10} className="text-indigo-400"/> {k}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-3">
                        <button 
                            onClick={() => handleEditClick(cat)} 
                            className="text-slate-400 hover:text-white"
                            title="Edit"
                        >
                            <Edit2 size={16}/>
                        </button>
                        <button 
                            onClick={() => handleDelete(cat.id)} 
                            className="text-slate-400 hover:text-red-400"
                            title="Delete"
                        >
                            <Trash2 size={16}/>
                        </button>
                    </div>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-8 text-center text-slate-500">No categories found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CategoryView;
